/**
 * Maps dynamic field labels to the backend's hardcoded column names.
 * The backend stores option IDs in specific columns (service_name_id, reference_id, etc.)
 * and returns labels via JOINs (service_name_label, reference_label, etc.)
 *
 * This mapping bridges the gap between dynamic field labels and the fixed DB schema.
 */

const FIELD_COLUMN_MAP = {
  "service name": {
    id_column: "service_name_id",
    label_column: "service_name_label",
    text_column: "service_name",
  },
  "business category": {
    id_column: "business_category_id",
    label_column: "business_category_label",
    text_column: "business_category",
  },
  "industry field": {
    id_column: "industry_field_id",
    label_column: "industry_field_label",
    text_column: "industry_field",
  },
  // Common aliases (different industry templates use different labels)
  "industry": {
    id_column: "industry_field_id",
    label_column: "industry_field_label",
    text_column: "industry_field",
  },
  "lead source": {
    id_column: "reference_id",
    label_column: "reference_label",
    text_column: "reference",
  },
  // Real estate specific
  "property type": {
    id_column: "service_name_id",
    label_column: "service_name_label",
    text_column: "service_name",
  },
  // Common across industries
  "service type": {
    id_column: "service_name_id",
    label_column: "service_name_label",
    text_column: "service_name",
  },
  "course / program": {
    id_column: "service_name_id",
    label_column: "service_name_label",
    text_column: "service_name",
  },
  "patient type": {
    id_column: "business_category_id",
    label_column: "business_category_label",
    text_column: "business_category",
  },
  "student category": {
    id_column: "business_category_id",
    label_column: "business_category_label",
    text_column: "business_category",
  },
  "client type": {
    id_column: "business_category_id",
    label_column: "business_category_label",
    text_column: "business_category",
  },
  "customer type": {
    id_column: "business_category_id",
    label_column: "business_category_label",
    text_column: "business_category",
  },
  "shipment type": {
    id_column: "industry_field_id",
    label_column: "industry_field_label",
    text_column: "industry_field",
  },
  "project type": {
    id_column: "industry_field_id",
    label_column: "industry_field_label",
    text_column: "industry_field",
  },
  "destination type": {
    id_column: "industry_field_id",
    label_column: "industry_field_label",
    text_column: "industry_field",
  },
  "product category": {
    id_column: "industry_field_id",
    label_column: "industry_field_label",
    text_column: "industry_field",
  },
  "purpose": {
    id_column: "industry_field_id",
    label_column: "industry_field_label",
    text_column: "industry_field",
  },
  "budget range": {
    id_column: null,
    label_column: null,
    text_column: null,
  },
};

/**
 * Get the DB column mapping for a dynamic field label.
 * Returns { id_column, label_column, text_column } or null if no mapping exists.
 */
export function getFieldMapping(fieldLabel) {
  return FIELD_COLUMN_MAP[fieldLabel.toLowerCase()] || null;
}

/**
 * Get the display value for a dynamic field from a lead object.
 */
export function getFieldDisplayValue(lead, fieldLabel, fieldOptions) {
  if (!lead) return "—";

  const mapping = getFieldMapping(fieldLabel);

  if (mapping) {
    // 1. Check resolved label from backend JOIN
    if (lead[mapping.label_column]) return lead[mapping.label_column];

    // 2. Check if _id exists and match against options
    if (lead[mapping.id_column] && fieldOptions?.length > 0) {
      const match = fieldOptions.find((o) => o.id === lead[mapping.id_column]);
      if (match) return match.option_label;
    }

    // 3. Fallback to raw text column
    if (lead[mapping.text_column]) return lead[mapping.text_column];
  }

  // 4. Try generic snake_case match
  const key = fieldLabel.toLowerCase().replace(/\s+/g, "_");
  if (lead[`${key}_label`]) return lead[`${key}_label`];
  if (lead[key]) return lead[key];

  return "—";
}

/**
 * Get the current option ID for a dynamic field from a lead object.
 */
export function getFieldOptionId(lead, fieldLabel) {
  if (!lead) return "";

  const mapping = getFieldMapping(fieldLabel);
  if (mapping && lead[mapping.id_column]) {
    return lead[mapping.id_column];
  }

  // Generic fallback
  const key = fieldLabel.toLowerCase().replace(/\s+/g, "_");
  if (lead[`${key}_id`]) return lead[`${key}_id`];

  return "";
}

/**
 * Build the payload key for saving a dynamic field option ID.
 * Maps "Lead Source" → "reference_id", "Service Name" → "service_name_id", etc.
 * Returns null if no DB column exists for this field.
 */
export function getFieldPayloadKey(fieldLabel) {
  const mapping = getFieldMapping(fieldLabel);
  if (mapping) return mapping.id_column; // can be null for unmapped fields

  return null;
}

/**
 * Build payload entries for a dynamic field selection.
 * Returns an object with the appropriate keys, or null if no DB mapping.
 * For mapped fields: { service_name_id: "optId" }
 * For unmapped fields: returns null (caller should store in notes)
 */
export function buildFieldPayload(fieldLabel, optionId, optionLabel) {
  const mapping = getFieldMapping(fieldLabel);

  if (mapping && mapping.id_column) {
    const result = { [mapping.id_column]: optionId };
    // Also set the text column with the label for backward compat
    if (mapping.text_column) {
      result[mapping.text_column] = optionLabel || null;
    }
    return result;
  }

  return null; // no DB column for this field
}
